import { now } from '../Clock.ts';
import {
  BaseStimulator,
  type StimulatorKind,
  type StimulusRequest,
  type StimulusResult,
} from './Stimulator.ts';

/**
 * The default stimulator, and the one the application always starts with.
 *
 * It connects instantly, can be armed, accepts requests, and reports pulses
 * starting and ending -- and does absolutely nothing else. No hardware, no
 * simulated participant, no button press, therefore no flap.
 *
 * That last part is intentional and is worth stating plainly: with the
 * MockStimulator selected, the bot will request stimulation, the log will fill
 * with a complete STIM_REQUESTED / STIM_STARTED / STIM_ENDED chain, and the
 * bird will fall out of the sky. That is the correct behaviour. The game
 * responds to detected movement, not to the intention to cause movement, and
 * this stimulator is the cheapest possible demonstration of the difference.
 */
export class MockStimulator extends BaseStimulator {
  readonly kind: StimulatorKind = 'mock';
  readonly label = 'Mock stimulator (no output)';
  readonly simulated = true;

  /** Artificial acknowledgement delay, ms. Models transport, not physiology. */
  constructor(private readonly ackDelayMs = 0) {
    super();
  }

  async connect(): Promise<void> {
    this.connected = true;
  }

  async disconnect(): Promise<void> {
    this.connected = false;
    this.armed = false;
  }

  async requestStimulus(request: StimulusRequest): Promise<StimulusResult> {
    const denied = this.guard(request);
    if (denied) return denied;

    this.requestCount++;
    this.acceptedCount++;
    this.lastRequestAt = request.requestedAt;

    if (this.ackDelayMs > 0) {
      await new Promise((r) => setTimeout(r, this.ackDelayMs));
    }

    const startedAt = now();
    this.lastStimulusAt = startedAt;
    this.deliveredCount++;

    return {
      requestId: request.requestId,
      accepted: true,
      startedAt,
      endedAt: startedAt + request.durationMs,
    };
  }

  protected override detail(): Record<string, unknown> {
    return { ackDelayMs: this.ackDelayMs, note: 'no physical output; no input produced' };
  }
}
